import React, { useRef } from 'react';
import { ALL_PROPERTY_FACILITIES } from '../../../lib/constants';
import { OnboardingDraft } from './types';
import { Textarea } from '../../ui/Textarea';
import { Callout } from '../../ui/Callout';
import { Star } from '@phosphor-icons/react';
interface StepBioAmenitiesProps {
  data: Partial<OnboardingDraft>;
  updateData: (data: Partial<OnboardingDraft>) => void;
}
// Double-tap detection window (ms). Wider so a slightly slower second tap still promotes the pill.
const DOUBLE_TAP_MS = 500;
export function StepBioAmenities({
  data,
  updateData
}: StepBioAmenitiesProps) {
  const bioLength = data.bio?.length || 0;
  const isBioGood = bioLength >= 150;
  const lastTapRef = useRef<Record<string, number>>({});
  const amenities = data.amenities || [];
  const required = data.requiredAmenities || [];
  const handleAmenityTap = (val: string) => {
    const now = Date.now();
    const last = lastTapRef.current[val] || 0;
    const isDoubleTap = now - last < DOUBLE_TAP_MS;
    lastTapRef.current[val] = now;
    const isDesired = amenities.includes(val);
    const isRequired = required.includes(val);
    if (isDoubleTap && isDesired && !isRequired) {
      // Promote to obligatoriu
      updateData({
        requiredAmenities: [...required, val]
      });
      return;
    }
    if (isRequired) {
      // Already obligatoriu → deselect entirely
      updateData({
        amenities: amenities.filter(a => a !== val),
        requiredAmenities: required.filter(a => a !== val)
      });
      return;
    }
    if (isDesired) {
      // Was just desired → deselect
      updateData({
        amenities: amenities.filter(a => a !== val)
      });
      return;
    }
    // First tap — add to desired
    updateData({
      amenities: [...amenities, val]
    });
  };
  return <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1530">
      <div data-id="element-1531">
        <h2 className="text-2xl font-bold text-light-1" data-id="element-1532">Ultimele detalii</h2>
      </div>

      {/* PRIMARY: Amenity filters */}
      <div className="space-y-4" data-id="element-1533">
        <div data-id="element-1534">
          <h3 className="text-lg font-semibold text-light-1" data-id="element-1535">
            Ce trebuie neapărat să aibă locuința?
          </h3>
          <p className="text-sm text-light-3 mt-1" data-id="element-1536">
            Alege doar ce e cu adevărat important pentru tine, pentru a nu
            limita numărul ofertelor.
          </p>
        </div>

        <Callout variant="info" data-id="element-1537">
          Apasă o dată ={' '}
          <span className="text-lime-1 font-medium" data-id="element-1538">îmi doresc</span>. Apasă de
          două ori <span className="font-semibold" data-id="element-1539">repede</span> ={' '}
          <span className="text-lime-1 font-medium" data-id="element-1540">obligatoriu</span>.
        </Callout>

        <div className="flex flex-wrap gap-2" data-id="element-1541">
          {ALL_PROPERTY_FACILITIES.map(amenity => {
          const isDesired = amenities.includes(amenity);
          const isRequired = required.includes(amenity);
          return <button key={amenity} onClick={() => handleAmenityTap(amenity)} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${isRequired ? 'bg-lime-1 border-lime-1 text-dark-1' : isDesired ? 'bg-lime-4 border-lime-1 text-lime-1' : 'bg-dark-2 border-dark-3 text-light-3 hover:border-dark-4 hover:text-light-2'}`} aria-label={isRequired ? `${amenity} — obligatoriu. Apasă pentru a deselecta.` : isDesired ? `${amenity} — îmi doresc. Apasă de două ori pentru obligatoriu.` : `${amenity} — apasă pentru a selecta.`} data-id="element-1542">
                {isRequired && <Star size={12} weight="fill" data-id="element-1543" />}
                {amenity}
              </button>;
        })}
        </div>
      </div>

      {/* SECONDARY: Optional extra details */}
      <div className="pt-6 border-t border-dark-3 space-y-4" data-id="element-1544">
        <div data-id="element-1545">
          <h3 className="text-lg font-semibold text-light-1" data-id="element-1546">
            Vrei să adaugi ceva în plus?
          </h3>
          <p className="text-sm text-light-3 mt-1" data-id="element-1547">
            Opțional — orice detaliu care nu a fost menționat până acum.
          </p>
        </div>

        <div className="relative" data-id="element-1548">
          <Textarea value={data.bio || ''} onChange={e => updateData({
          bio: e.target.value
        })} placeholder="ex: caut neapărat etaj 1 sau lift, mă mut cu fiul meu, lucrez de acasă..." rows={5} maxLength={150} className="bg-dark-2 border-dark-3 resize-none pb-8" data-id="element-1549" />
          <div className={`absolute bottom-3 right-3 text-xs font-medium ${isBioGood ? 'text-lime-1' : 'text-light-3'}`} data-id="element-1550">
            {bioLength} / 150
          </div>
        </div>

        <Callout variant="tip" dismissible dismissKey="bio-extra-details-tip" data-id="element-1551">
          Spune-le proprietarilor orice nu a fost menționat până acum — cerințe
          speciale, context personal sau preferințe la care nu faci
          compromisuri.
        </Callout>
      </div>
    </div>;
}